import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Heading,
  Progress,
  Stat,
  Text,
  useTheme,
  useColorModeValue,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useDisclosure,
} from "@chakra-ui/react";
import {
  CollecticonChevronDownSmall,
  CollecticonMap,
} from "@devseed-ui/collecticons-chakra";
import { LngLatBoundsLike } from "react-map-gl";
import bbox from "@turf/bbox";

import {
  LoadingDetectionCard,
  DetectionCard,
  Map,
  Error,
  Loading,
  MapScenePreview,
  MapSpeciesLegend,
  InferencesLayer,
  SceneStatusTag,
  DownloadDetections,
  StatsCard,
  StatsNumber,
  StatsLabel,
  Overline,
  LoadingStatsCard,
} from "../../components";
import MetaDataCard, {
  MetaData,
  MetaDataHeader,
} from "../../components/MetaDataCard";
import Panel, {
  PanelBody,
  PanelFooter,
  PanelHeader,
} from "../../components/Panel";
import { RunModel } from "./components";
import { ModelRun } from "../../types/scenes";
import { formatDate, getLikelySpecies, getSortedSpecies } from "../../utils";
import { useScene, useSceneInferences } from "../../hooks";
import { Inference } from "../../types/inferences";
import useInfrenceSummary from "./hooks/useInferenceSummary";
import ImageControls from "../../components/ImageControls";
import { DEFAULT_BRIGHTNESS, DEFAULT_CONTRAST } from "../../settings";
import { ModelStatusTag } from "../../components/SceneStatusTag";
import { PANEL_SIZE } from "../../theme";

function SceneDetail() {
  const { sceneId } = useParams();
  const theme = useTheme();
  const {
    scene,
    error: sceneError,
    isPending: sceneIsPending,
    refetch,
  } = useScene(sceneId);
  const {
    inferences,
    error: inferencesError,
    isPending: inferencesIsPending,
    validate,
  } = useSceneInferences(sceneId);
  const [validatingIds, setValidatingIds] = useState<number[]>([]);
  const { count, validated, species, modelRuns } = useInfrenceSummary(
    inferences?.features
  );
  const inferencesSorted = useMemo(
    () =>
      inferences?.features.sort(
        (inferenceA: Inference, inferenceB: Inference) =>
          Number(getLikelySpecies(inferenceB).confidence) -
          Number(getLikelySpecies(inferenceA).confidence)
      ),
    [inferences]
  );
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [brightness, setBrightness] = useState<number>(DEFAULT_BRIGHTNESS);
  const [contrast, setContrast] = useState<number>(DEFAULT_CONTRAST);
  const topSpecies = useMemo(() => {
    return getSortedSpecies(species);
  }, [species]);

  const detectionHeadingBg = useColorModeValue("#e7e8ea", "base.700");
  const mapLegendBg = useColorModeValue("base.50", "base.500");

  const bounds: LngLatBoundsLike | undefined = useMemo(() => {
    if (!scene?.geometry) return;
    const [x1, y1, x2, y2] = bbox(scene);
    return [x1, y1, x2, y2];
  }, [scene]);

  if (sceneIsPending) {
    return <Loading mt="8" />;
  }

  if (!sceneId) {
    return <Error>Not found</Error>;
  }

  if (sceneError || inferencesError) {
    return <Error>{sceneError?.message || inferencesError?.message}</Error>;
  }
  const { is_valid, validation_error } = scene.properties;
  const hasSuccessModelRuns = scene.properties.model_runs.some(({ status }: ModelRun) => status === "succeeded");
  const validateInference = async (validationId: number, isValid: boolean) => {
    setValidatingIds((prev) => [...prev, validationId]);
    await validate(validationId, isValid);
    setValidatingIds((prev) => prev.filter((i) => i !== validationId));
  };

  return (
    <Box
      display="grid"
      position="relative"
      gridTemplateColumns={`${PANEL_SIZE} 1fr`}
      overflow="hidden"
    >
      <Panel>
        <PanelHeader
          subhead="Scene"
          heading={formatDate(new Date(Date.parse(scene.properties.date)))}
          closeLink="/scenes/"
          closeLabel="Return to the scenes dashboard"
        >
          <SceneStatusTag scene={scene} />
        </PanelHeader>
        <PanelBody>
          {hasSuccessModelRuns && 
          <StatsCard>
            {inferencesIsPending ? (
              Array.from(Array(3)).map((i, ind) => (
                // eslint-disable-next-line react/no-array-index-key
                <LoadingStatsCard key={ind} />
              ))
            ) : topSpecies.length > 0 ? (
              <>
                {topSpecies.map(([label, value]) => (
                  <Stat key={label} textAlign="right" flex="initial">
                    <StatsNumber>{value}</StatsNumber>
                    <StatsLabel>{label}</StatsLabel>
                  </Stat>
                ))}
                <Stat textAlign="right" flex="initial">
                  <StatsNumber>{validated}</StatsNumber>
                  <StatsLabel>Validated</StatsLabel>
                </Stat>
              </>
            ) : (
              <Overline>0 species detected</Overline>
            )}
          </StatsCard>}

          <MetaDataCard>
            <MetaDataHeader as="h3">Scene Metadata</MetaDataHeader>
            <MetaData
              label="Scene Id"
              value={`${scene.properties.s3_prefix.substring(0, 20)}...`}
            />
            <MetaData
              label="Aquired"
              value={formatDate(new Date(Date.parse(scene.properties.date)))}
            />
            <MetaData
              label="Model Runs"
              value={scene.properties.model_runs.length}
            />
            <MetaData label="Detections" value={count} />
            {count > 0 && (
              <>
                <Text mt={2} fontSize="sm" opacity="0.875">
                  Validated Detections ({validated} of {count})
                </Text>
                <Progress
                  value={(validated / count) * 100}
                  colorScheme="primary"
                  size="sm"
                  borderRadius="4"
                />
              </>
            )}
          </MetaDataCard>

          <RunModel sceneId={sceneId} onSubmit={refetch} />

          {scene.properties.model_runs.map((run: ModelRun) => {
            return (
              <MetaDataCard key={run.id}>
                <MetaDataHeader as="h3">Model Results</MetaDataHeader>
                <MetaData
                  label="Model"
                  value={`${run.model.name} (${run.model.version})`}
                />
                <MetaData
                  label="Last Run"
                  value={formatDate(new Date(Date.parse(run.date)))}
                />
                <Box display="flex" fontSize="sm" gap="4" alignItems="cen†er">
                  <Box as="span" opacity="0.875" flex="1">
                    Status
                  </Box>
                  <ModelStatusTag run={run} />
                </Box>
                <MetaData
                  label="Detections"
                  value={modelRuns[run.id]?.count || 0}
                />
              </MetaDataCard>
            );
          })}
        </PanelBody>
        <PanelFooter>
          <Button
            variant="base-soft-outline"
            rightIcon={<CollecticonMap />}
            as={Link}
            to={`/scenes/${sceneId}/validate/`}
            isDisabled={!inferences?.features.length}
          >
            Validate in Map
          </Button>
          <DownloadDetections
            inferences={inferences}
            fileName={`scene-${
              scene.properties.date.split("Z")[0]
            }-inferences.csv`}
            isDisabled={!inferences?.features.length}
          >
            Download Scene Detections
          </DownloadDetections>
        </PanelFooter>
      </Panel>
      <Box display="grid" gridTemplateRows="1fr 1fr" overflow="auto">
        <Map bounds={bounds}>
          <Card
            position="absolute"
            bottom={10}
            w="fit-content"
            minW="12rem"
            maxHeight="18rem"
            overflow="auto"
            left={2.5}
            p={3}
            fontFamily={theme.fonts.body}
            zIndex="1020"
            background={mapLegendBg}
            border="1px solid var(--chakra-colors-chakra-border-color)"
          >
            <MapSpeciesLegend />
          </Card>
          {!validation_error && is_valid && (
            <MapScenePreview
              id={sceneId}
              brightness={brightness}
              contrast={contrast}
            />
          )}
          {inferencesIsPending && (
            <Loading
              fontSize="sm"
              position="absolute"
              fontFamily={theme.fonts.body}
              top={6}
              right={6}
              display="flex"
              alignItems="center"
              gap={4}
              fontWeight="semibold"
            >
              Loading detections
            </Loading>
          )}
          {inferences && <InferencesLayer id="inferences" data={inferences} />}
        </Map>
        <Box
          p="4"
          borderTop="1px solid"
          borderColor="base.200a"
          overflow="auto"
          bg="base.100a"
        >
          <Box
            position="sticky"
            top="-1rem"
            zIndex="1005"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            background={detectionHeadingBg}
            margin="-1rem -1rem 1rem"
            padding="1rem 1rem"
            borderBottom="1px solid"
            borderColor={mapLegendBg}
          >
            <Heading as="h3" fontSize="lg">
              Detections
            </Heading>
            <Popover isOpen={isOpen} onClose={onClose}>
              <PopoverTrigger>
                <Button
                  rightIcon={<CollecticonChevronDownSmall />}
                  onClick={onToggle}
                  variant="primary-soft-outline"
                  size="sm"
                >
                  Image Controls
                </Button>
              </PopoverTrigger>
              <PopoverContent minW="fit-content" p={4} pb={6}>
                <ImageControls
                  brightness={brightness}
                  setBrightness={setBrightness}
                  contrast={contrast}
                  setContrast={setContrast}
                />
              </PopoverContent>
            </Popover>
          </Box>
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fill, minmax(12rem,1fr))"
            gap="4"
          >
            {inferencesSorted?.length ? (
              inferencesSorted.map((inference: Inference) => (
                <Card
                  key={inference.properties.id}
                  fontSize="sm"
                  position="relative"
                  display="flex"
                  flexDirection="column"
                  overflow="hidden"
                >
                  <DetectionCard
                    inference={inference}
                    validate={validateInference}
                    isValidating={validatingIds.includes(
                      inference.properties.id
                    )}
                    includeThumbnail
                    brightness={brightness}
                    contrast={contrast}
                  />
                </Card>
              ))
            ) : (
              <>
                {inferencesIsPending ? (
                  <Loading opacity="0.75" fontSize="sm" gridColumn="1 / -1">
                    Fetching detections
                  </Loading>
                ) : (
                  <Heading as="h4" opacity="0.75" size="sm" gridColumn="1 / -1">
                    {hasSuccessModelRuns ? "No species detected" : "No successful model runs"}
                  </Heading>
                )}
                {Array.from(Array(10)).map((a, ind) => (
                  <Card
                    // eslint-disable-next-line react/no-array-index-key
                    key={ind}
                    opacity="0.5"
                  >
                    <LoadingDetectionCard noData={!inferencesIsPending} />
                  </Card>
                ))}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default SceneDetail;
